import React, { useEffect, useContext, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import '../CreatePlanPage.scss';
import {
  Journey,
  PageAbsolutePath,
  PageRelativePath
} from '../../../consts/Common';
import { ButtonContinue } from '../../common/Button/ButtonContinue';
import { ButtonBack } from '../../common/Button/ButtonBack';
import Summary, {
  SummaryCustomerAcceptance
} from '../../common/Summary/Summary';
import { SiteContext } from '../../../context/SiteContext';
import PageHeader from '../../common/PageHeader';
import { PaymentMethod } from '../../common/Constants';
import { useJourney } from '../Layout';
import { useTranslation } from 'react-i18next';
import { LocationVehicleId } from './CustomerVehicleDetailsPage';
import { PaymentApi } from '../../../api/PaymentApi';
import {
  Totals,
  PaymentAssistCustomer,
  PaymentAssistRequest,
  PaymentAssistBeginResponse
} from '../../common/CommonTypes';
import {
  calculateTotals,
  getSelectedProducts
} from '../../common/ProductMethods';

interface ValidationResultObject {
  isValid: boolean;
  validationMessage: string;
}
interface customerAcceptanceErrorObject {
  customerAcceptanceError: string;
}

const CustomerSummaryPage = (): JSX.Element => {
  const navigate = useNavigate();

  const {
    journey,
    onUpdateProducts,
    onUpdateAgreedTermsAndConditions,
    onPageChange
  } = useJourney();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const isOnlySinglePaymentProducts = () => {
    const selectedProducts = products.filter((p) => p.isSelected);

    return selectedProducts.every(
      (p) => p.selectedPaymentMethod === PaymentMethod.Single
    );
  };

  const isOnlyPaymentAssistProducts = () => {
    const selectedProducts = products.filter((p) => p.isSelected);

    return selectedProducts.every(
      (p) => p.selectedPaymentMethod === PaymentMethod.PaymentAssist
    );
  };

  const {
    vehicle,
    personalDetail,
    products,
    agreedTermsAndConditions,
    singlePaymentDetails
  } = journey;

  const { site } = useContext(SiteContext);
  const { journeyType } = site;
  const cms = site.content.summaryPage;
  const { t } = useTranslation();
  const { state } = useLocation();

  const isCallCentreUser = !!site.user?.userId;

  const onBack = () => {
    navigate(PageRelativePath.PersonalDetails, {
      state: {
        vehicleIdType: (state as LocationVehicleId).vehicleIdType
      }
    });
    onPageChange(PageAbsolutePath.Customer.PersonalDetails, true);
  };

  const [newValidationErrors, setNewValidationErrors] =
    useState<customerAcceptanceErrorObject>({
      customerAcceptanceError: ''
    });

  const validateCustomerAcceptance = (): ValidationResultObject => {
    let validationMessage = '';

    if (!agreedTermsAndConditions && !isCallCentreUser) {
      validationMessage = t('summary.customer-acceptance-error');
    }

    if (validationMessage === '') {
      return {
        isValid: true,
        validationMessage
      };
    } else {
      return {
        isValid: false,
        validationMessage
      };
    }
  };

  const selectedProducts = getSelectedProducts(products);
  const totals: Totals = calculateTotals(selectedProducts, 1);

  const callPaymentAssistRedirect = () => {
    const token = journey.token || site.user.token;

    const { billingAddress } = singlePaymentDetails;

    const selectedVariant = selectedProducts[0].productVariants.find(
      (v) => v.isSelected
    );

    const paymentInfo: PaymentAssistCustomer = {
      amount: totals.selectedTotalPrice,
      productVariant: selectedVariant?.variantId ?? '',
      surname: personalDetail.surname,
      firstName: personalDetail.firstName,
      address1: billingAddress.addressLine1 ?? '',
      address2: billingAddress.addressLine2 ?? '',
      city: billingAddress.townOrCity ?? '',
      postCode: billingAddress.postCode ?? '',
      county: billingAddress.region ?? '',
      phone: personalDetail.phone,
      email: personalDetail.email,
      vrn: vehicle?.vehicleDetails?.vrn ?? ''
    };

    const paymentAssistRequest: PaymentAssistRequest = {
      paymentInfo,
      site,
      journey,
      journeyType
    };

    const controller = new AbortController();

    const successCallback = (data: PaymentAssistBeginResponse) => {
      window.localStorage.setItem('redirectToPA', 'true');
      window.location.href = data.url;
    };

    const failureCallback = () => {
      console.log('failure');
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      navigate(PageRelativePath.PaymentAssistFailure, {
        relative: 'path'
      });
    };

    new PaymentApi().beginPaymentAssistRequest(
      paymentAssistRequest,
      token,
      successCallback,
      failureCallback,
      controller.signal
    );

    return () => {
      controller.abort();
    };
  };

  const onCheckout = () => {
    const result: ValidationResultObject = validateCustomerAcceptance();

    if (result.isValid) {
      if (isOnlySinglePaymentProducts()) {
        navigate(PageRelativePath.SinglePayment, {
          state: {
            vehicleIdType: (state as LocationVehicleId).vehicleIdType
          }
        });
      } else if (isOnlyPaymentAssistProducts()) {
        callPaymentAssistRedirect();
      } else {
        navigate(PageRelativePath.DirectDebit, {
          state: {
            vehicleIdType: (state as LocationVehicleId).vehicleIdType
          }
        });
      }
      onPageChange(PageAbsolutePath.Customer.Summary, true);
    } else {
      setNewValidationErrors({
        ...newValidationErrors,
        customerAcceptanceError: result.validationMessage
      });
    }
  };

  const customerAcceptanceText: SummaryCustomerAcceptance = {
    sections: cms.acceptanceText
  };

  return (
    <div>
      <div>
        <PageHeader
          title={cms.title}
          description={cms.description}
          information={cms.information}
          warning={cms.warning}
          descriptionClass='p2'
        />
        <Summary
          vehicle={vehicle}
          personalDetail={personalDetail}
          products={products}
          agreedTermsAndConditions={agreedTermsAndConditions}
          onUpdateAgreedTermsAndConditions={onUpdateAgreedTermsAndConditions}
          onUpdateProducts={onUpdateProducts}
          onPageChange={onPageChange}
          journeyType={Journey.Customer}
          policyStartDate={new Date()}
          error=''
          isRenewals={false}
          showTermsAndConditions={!isCallCentreUser}
          personalDetailsPageUrl={PageRelativePath.PersonalDetails}
          vehiclePageUrl={PageRelativePath.VehicleDetails}
          productPageUrl={PageRelativePath.Products}
          createPlanPageUrl={PageRelativePath.CreatePlan}
          customerAcceptanceText={customerAcceptanceText}
          customerAcceptanceSectionExpandedOnLoad={true}
          customerAcceptanceTickboxText={cms.acceptanceTickboxText}
          renewalMarketingTickboxText={cms.consentToRenewalMarketingTickboxText}
          culture={site.cultureCode}
          countryCode={site.countryCode}
          phoneNumberLabel={site.content.customerPage.phoneNumberLabel}
        />
      </div>
      <div className='error-style'>
        {newValidationErrors.customerAcceptanceError}
      </div>
      <div className='flex-row form-nav'>
        <ButtonBack
          name='btnBack'
          onClick={onBack}
          text={t('customer-button.back-buttont-text')}
        />
        <ButtonContinue
          name='btn-see-products'
          text={t('customer-button.button-checkout-text')}
          onClick={onCheckout}
          additionalClassNames='m05-left'
        />
      </div>
    </div>
  );
};

export default CustomerSummaryPage;
